import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Typography, Drawer, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import CustomButton from '../customButton';
import Scrollbars from '../scrollbars';
import ActionButton from '../actionButton';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { ReactComponent as NoLevelIcon } from '../../../assets/icons/star_dark.svg';
import Tooltip from '../tooltip';
import { handleInstructionGuideDrawer } from '../../../store/modules/instructionGuide';
import { getInstructionGuideMenuItems } from '../../../utility/uiUtils';
import StorageService from '../../../utility/storageService';
import { AUTH } from '../../../utility/http';
import { setAuthInfo } from '../../../store/modules/auth';
import { APP_PAGES } from '../../../constants/pages';
import { useTranslations } from '../../../utility/useTranslations';
import { instructionGuideSelector } from '../../../store/selectors/instructionGuideSelector';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  middleWidth: {
    width: 940,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: 71,
    padding: spacing(5, 6),
    borderBottom: `1px solid ${primary.bluish5}`,
  },
  title: {
    marginRight: spacing(2),
    wordBreak: 'break-word',
  },
  scrollWrapper: {
    width: '100%',
    height: '100%',
    minHeight: 0,
  },
  content: {
    boxSizing: 'border-box',
    display: 'flex',
    width: '100%',
    height: '100%',
    minHeight: 0,
  },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
  },
  guideContentWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    padding: spacing(12),
    width: '100%',
    height: '100%',
    minWidth: 0,
  },
  guideWrapper: {
    width: '100%',
    height: '100%',
  },
  guideBlock: {
    boxSizing: 'border-box',
    backgroundColor: primary.bluish9,
    borderRight: `1px solid ${primary.bluish7}`,
    padding: spacing(6, 3, 0, 3),
    height: '100%',
  },
  guideMenu: {
    height: '100%',
    display: 'grid',
    gap: 24,
    alignContent: 'baseline',
    justifyContent: 'flex-start',
  },
  guideButton: {
    justifyContent: 'flex-start',
    width: 270,
    padding: spacing(1, 2, 1, 2),
    borderRadius: 8,
    '& span': {
      height: 24,
      color: primary.bluish3,
      textTransform: 'none',
      fontFamily: 'ProximaNova-Bold',
      fontSize: 15,
      fontWeight: 700,
      lineHeight: '24px',
    },
  },
  welcomeTitle: {
    marginBottom: spacing(6),
  },
  descriptionGuideBlock: {
    height: 220,
    justifyContent: 'space-between',
    display: 'grid',
    marginTop: spacing(6),
  },
  descriptionGuide: {
    fontFamily: 'ProximaNova-Regular',
    fontSize: 14,
    fontWeight: 400,
    color: '#41395F',
    lineHeight: '18px',
  },
  descriptionGuideBold: {
    fontFamily: 'ProximaNova-Bold',
  },
  videoWrapper: {
    width: 549,
    height: 345,
  },
  clickedGuide: {
    backgroundColor: primary.white,
  },
  fullHeight: {
    '& > div': {
      height: '100%',
    },
  },
  guideTooltipIcon: {
    width: 16,
    height: 16,
    marginLeft: spacing(1),
  },
  scrollY: {
    '& .os-scrollbar-vertical': {
      right: -42,
    },
  },
}));

const INITIAL_VIEW = 0;

const InstructionGuideDrawer = ({ auth }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translations = useTranslations(APP_PAGES.INSTRUCTION_GUIDE);
  const { instructionGuide } = useSelector(instructionGuideSelector);
  const isFirstTimeLogged = auth?.last_login === null;
  const isOpened = isFirstTimeLogged || instructionGuide;

  const contentButtons = getInstructionGuideMenuItems(
    translations.guideButtons,
    auth
  );
  const [guideContent, setGuideContent] = useState(contentButtons[0]);

  const renderContent = () => {
    const isInitialView = guideContent?.id === INITIAL_VIEW;
    return (
      <>
        <div className={classes.guideBlock}>
          <div className={classes.guideMenu}>
            {contentButtons.map(content => {
              const isClickedGuide = content.id === guideContent.id;

              return (
                <CustomButton
                  key={`guide_button_${content.id}`}
                  className={classNames(classes.guideButton, {
                    [classes.clickedGuide]: isClickedGuide,
                  })}
                  type="backNoIcon"
                  onClick={() => setGuideContent(content)}
                >
                  {content.name}
                  {content.hasTooltip ? (
                    <Tooltip
                      customIconClass={classes.guideTooltipIcon}
                      text={translations.guideTooltipText}
                      icon={NoLevelIcon}
                    />
                  ) : null}
                </CustomButton>
              );
            })}
          </div>
        </div>
        <div className={classes.guideContentWrapper}>
          <Scrollbars verticalScrollClass={classes.scrollY}>
            <div className={classes.guideWrapper}>
              <Typography className={classes.welcomeTitle} variant="h3">
                {guideContent.name}
              </Typography>
              <div className={classes.videoWrapper}>
                <iframe
                  title="guide"
                  style={{ border: 0 }}
                  width="549"
                  height="345"
                  src={guideContent.src}
                  allowFullScreen
                  // eslint-disable-next-line react/no-unknown-property
                  allowtransparency="true"
                />
              </div>
              {isInitialView ? (
                <div className={classes.descriptionGuideBlock}>
                  <Typography className={classes.descriptionGuide}>
                    {translations.descriptionInstructionGuideOne}
                  </Typography>
                  <Typography className={classes.descriptionGuide}>
                    {translations.descriptionInstructionGuideTwo}
                  </Typography>
                  <Typography className={classes.descriptionGuide}>
                    {translations.descriptionInstructionGuideThree}
                  </Typography>
                  <Typography
                    className={classNames(
                      classes.descriptionGuide,
                      classes.descriptionGuideBold
                    )}
                  >
                    {translations.descriptionInstructionGuideFour}
                  </Typography>
                </div>
              ) : null}
            </div>
          </Scrollbars>
        </div>
      </>
    );
  };

  const handleClose = () => {
    if (isFirstTimeLogged) {
      const { last_login, ...newAuth } = auth;

      StorageService.set(AUTH, JSON.stringify(newAuth));
      dispatch(setAuthInfo(newAuth));
    }
    setGuideContent(contentButtons[0]);
    dispatch(handleInstructionGuideDrawer(false));
  };

  return (
    <Drawer
      anchor="right"
      classes={{
        paper: classes.middleWidth,
      }}
      open={isOpened}
      onClose={() => handleClose()}
    >
      <div className={classes.header}>
        <Typography className={classes.title} variant="h4">
          {translations.instructionGuideTitle}
        </Typography>
        <div className={classes.headerActions}>
          <ActionButton
            type={ACTION_BUTTON_TYPES.CLOSE}
            onClickHandler={() => handleClose()}
          />
        </div>
      </div>
      <div className={classes.content}>{isOpened ? renderContent() : null}</div>
    </Drawer>
  );
};

InstructionGuideDrawer.propTypes = {
  auth: PropTypes.object.isRequired,
};

export default InstructionGuideDrawer;
